import type { GridApi, IRowNode } from 'ag-grid-community';
import { VantageNames } from '@/vantage/constants';
import { nameToReferenceKey } from '@/vantage/components/evidence_table/helpers/utilities';
import _ from 'lodash';
import type { Maybe } from '@/utilities.types';
import { isEmptyish } from '@/utilities/stringHelper.utilities';

export enum FilterMode {
  Default = 'Default',
  Capsule = 'Capsule',
  Condition = 'Condition',
  Asset = 'Asset',
}

export const FILTER_MODES = [FilterMode.Default, FilterMode.Capsule, FilterMode.Condition, FilterMode.Asset];
export const BLANK_LABEL = '(Blanks)';

// Type definitions for filter values
export type FilterPrimitive = string | number | boolean | null | undefined;
export type FilterValue = FilterPrimitive | FilterPrimitive[];

export interface FilterModel extends Record<string, unknown> {
  filterMode: FilterMode;
  // Persisted views will JSON.stringify the model; Sets become empty objects. Allow arrays too.
  selectedValues: Set<FilterPrimitive> | FilterPrimitive[] | Record<string, FilterPrimitive>;
}

export interface ActiveGroupings {
  hasConditionGrouping: boolean;
  hasAssetGrouping: boolean;
}

/**
 * Generates a stable key for use in Sets and Maps
 */
export const keyForValue = (v: FilterValue): string => {
  if (v === null || v === undefined) {
    return '__BLANK__';
  }

  if (typeof v === 'string') {
    return `s:${v}`;
  }

  if (typeof v === 'number') {
    return `n:${v}`;
  }

  if (typeof v === 'boolean') {
    return `b:${v}`;
  }

  return `o:${String(v)}`;
};

/**
 * Strips trailing asterisk from a string
 */
export const stripStar = (s?: string): string | undefined => (s ? s.replace(/\*$/, '') : s);

/**
 * Gets the column ID from a column object
 */
export const colIdOf = (col: { getColId?: () => string | undefined }): string | undefined => {
  return col?.getColId?.();
};

/**
 * Gets the column ID from a node's row group column
 */
export const nodeGroupColId = (n: IRowNode): string | undefined => {
  const col = n?.rowGroupColumn as { getColId?: () => string | undefined } | undefined;
  return col ? colIdOf(col) : undefined;
};

/**
 * Compares two column IDs for equality (ignoring trailing asterisks)
 */
export const sameColId = (a?: string, b?: string): boolean => {
  return stripStar(a) === stripStar(b);
};

/**
 * Finds the nearest ancestor group node for a given target column ID
 */
export const findAncestorWithColId = (n: IRowNode | null, targetColId: string): IRowNode | null => {
  let p: IRowNode | null = n;

  while (p) {
    if (p.group && sameColId(nodeGroupColId(p), targetColId)) {
      return p;
    }
    p = p.parent;
  }

  return null;
};

/**
 * Resolves the current column ID for a field name (fallback to field if not found)
 */
export const colIdForField = (api: GridApi, field: string): string => {
  const col = api.getColumn(field);
  return col ? colIdOf(col as { getColId?: () => string | undefined }) ?? field : field;
};

/**
 * Checks if a value matches the selected set
 */
export const matchesSelected = (availableValues: FilterValue, selectedValues: Set<FilterPrimitive>): boolean => {
  // Treat blanks uniformly
  if (isEmptyish(availableValues)) {
    return selectedValues.has(null) || selectedValues.has(undefined);
  }

  if (Array.isArray(availableValues)) {
    if (_.isEmpty(availableValues)) {
      return selectedValues.has(null) || selectedValues.has(undefined);
    }
    return availableValues.some((x) => selectedValues.has(x));
  }

  return selectedValues.has(availableValues);
};

/**
 * Resolves the group node for a given target group column ID (or null if not under that group)
 */
export const resolveGroupNode = (node: IRowNode, targetColId: string): IRowNode | null => {
  if (node.group && sameColId(nodeGroupColId(node), targetColId)) {
    return node;
  }
  return findAncestorWithColId(node.parent, targetColId);
};

/**
 * Matches a node against a grouped selection
 */
export const matchGroupedSelection = (
  node: IRowNode,
  targetColId: string,
  modelSelection: Set<FilterPrimitive>,
  getValueForNode: (n: IRowNode, useAggData?: boolean) => FilterValue,
): boolean => {
  const groupNode = resolveGroupNode(node, targetColId);

  if (!groupNode) {
    return false; // not under that group
  }

  const groupVal = getValueForNode(groupNode, true);

  return matchesSelected(groupVal, modelSelection);
};

/**
 * Gets the condition column ID for the API
 */
export const getConditionColId = (api: GridApi): string => {
  return colIdForField(api, nameToReferenceKey(VantageNames.Columns.Condition));
};

/**
 * Gets the asset column ID for the API
 */
export const getAssetColId = (api: GridApi): string => {
  return colIdForField(api, VantageNames.Columns.Asset);
};

/**
 * Extracts a Set from a model's selected values
 */
export const extractSelectionSet = (model: Maybe<FilterModel>): Set<FilterPrimitive> => {
  if (!model || !model.selectedValues) {
    return new Set();
  }

  const raw = model.selectedValues;

  if (raw instanceof Set) {
    return raw as Set<FilterPrimitive>;
  }

  if (Array.isArray(raw)) {
    return new Set(raw);
  }

  if (typeof raw === 'object') {
    return new Set(Object.values(raw) as FilterPrimitive[]);
  }

  return new Set();
};

/**
 * Converts a model to a serializable format for persistence
 */
export const modelToSerializable = (mode: FilterMode, selected: Set<FilterPrimitive>): FilterModel | null => {
  // Convert Set to array for serialization safety (views persistence)
  const serializable = Array.from(selected);

  if (selected.size === 0) {
    return null;
  }

  return {
    filterMode: mode,
    selectedValues: serializable,
  } as FilterModel;
};

/**
 * Collector for distinct filter values
 */
export interface DistinctValueCollector {
  values: Set<FilterPrimitive>;
  sawBlank: boolean;
}

/**
 * Creates a new value collector
 */
export const createValueCollector = (): DistinctValueCollector => {
  return { values: new Set(), sawBlank: false };
};

/**
 * Adds a value to the collector, handling blanks and arrays
 */
export const addValueToCollector = (collector: DistinctValueCollector, value: FilterValue): void => {
  if (isEmptyish(value)) {
    collector.sawBlank = true;
    return;
  }

  if (Array.isArray(value)) {
    let hadArrayBlank = false;
    for (const v of value) {
      if (isEmptyish(v)) {
        hadArrayBlank = true;
      } else {
        collector.values.add(v);
      }
    }
    if (hadArrayBlank) {
      collector.sawBlank = true;
    }
    return;
  }

  collector.values.add(value);
};

/**
 * Collects default values from leaf nodes and group aggregates
 */
export const collectDefaultValues = (
  api: GridApi,
  getValueForNode: (node: IRowNode, useAggData?: boolean) => FilterValue,
  collector: DistinctValueCollector,
): void => {
  // Leaf values
  api.forEachLeafNode((node) => {
    addValueToCollector(collector, getValueForNode(node, false));
  });

  // Condition and Asset group agg values
  api.forEachNode((node) => {
    if (node.group && sameColId(nodeGroupColId(node), getConditionColId(api))) {
      addValueToCollector(collector, getValueForNode(node, true));
    }
    if (node.group && sameColId(nodeGroupColId(node), getAssetColId(api))) {
      addValueToCollector(collector, getValueForNode(node, true));
    }
  });
};

/**
 * Collects values from leaf nodes only (capsule mode)
 */
export const collectCapsuleValues = (
  api: GridApi,
  getValueForNode: (node: IRowNode, useAggData?: boolean) => FilterValue,
  collector: DistinctValueCollector,
): void => {
  api.forEachLeafNode((node) => {
    addValueToCollector(collector, getValueForNode(node, false));
  });
};

/**
 * Collects values from nodes grouped by a specific column
 */
export const collectGroupedValues = (
  api: GridApi,
  targetColId: string,
  getValueForNode: (node: IRowNode, useAggData?: boolean) => FilterValue,
  collector: DistinctValueCollector,
): void => {
  api.forEachNode((node) => {
    if (node.group && sameColId(nodeGroupColId(node), targetColId)) {
      addValueToCollector(collector, getValueForNode(node, true));
    }
  });
};

/**
 * Options for finalizing collected values
 */
export interface FinalizeValuesOptions {
  collector: DistinctValueCollector;
  seedValues?: FilterPrimitive[];
  filterMode: FilterMode;
  field: string;
  ensureBlanks?: boolean;
}

/**
 * Processes collected values into final sorted array with filtering
 */
export const finalizeCollectedValues = (options: FinalizeValuesOptions): FilterPrimitive[] => {
  const { collector, seedValues, filterMode, ensureBlanks = false } = options;

  // Add seed values if provided
  if (seedValues) {
    for (const v of seedValues) {
      if (isEmptyish(v)) {
        collector.sawBlank = true;
      } else {
        collector.values.add(v);
      }
    }
  }

  // Drop "multiple values" placeholders for boolean-y columns in group modes
  const inGroupMode = filterMode === FilterMode.Condition || filterMode === FilterMode.Asset;
  let values = Array.from(collector.values).filter((v) => {
    if (!inGroupMode) {
      return true;
    }
    return !(typeof v === 'string' && v.toLowerCase().includes('multiple'));
  });

  values.sort();

  // Ensure a single canonical null entry if any null/undefined was seen or if blanks should always be available
  if (collector.sawBlank || ensureBlanks) {
    values = [null, ...values];
  }

  return values;
};

export const getActiveGroupings = (api: GridApi): ActiveGroupings => {
  const columnState = api.getColumnState();
  const conditionColId = getConditionColId(api);
  const assetColId = getAssetColId(api);

  const groupedColIds = columnState.filter((c) => c.rowGroup).map((c) => stripStar(c.colId));
  const hasConditionGrouping = groupedColIds.some((id) => sameColId(id, conditionColId));
  const hasAssetGrouping = groupedColIds.some((id) => sameColId(id, assetColId));

  return { hasConditionGrouping, hasAssetGrouping };
};
